import { useLayoutEffect, useRef, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  children: ReactNode;
  className: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
  layerClassName?: string;
  onClose: () => void;
};

const FOCUSABLE = 'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex]:not([tabindex="-1"])';
let openModalCount = 0;
let lockedScrollY = 0;
let previousBodyStyles: Partial<CSSStyleDeclaration> = {};
let previousRootOverflow = '';

export function Modal({ ariaLabel, ariaLabelledBy, children, className, initialFocusRef, layerClassName = '', onClose }: Props) {
  const surfaceRef = useRef<HTMLDialogElement>(null);

  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    const root = document.getElementById('root');
    const restoreFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (openModalCount === 0) lockPage(root);
    openModalCount += 1;

    const focusTarget = matchMedia('(pointer: fine)').matches ? initialFocusRef?.current : null;
    (focusTarget ?? surface)?.focus({ preventScroll: true });

    return () => {
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) {
        unlockPage(root);
        restoreFocus?.focus({ preventScroll: true });
      }
    };
  }, [initialFocusRef]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const controls = [...event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((control) => !control.hidden);
    if (!controls.length) {
      event.preventDefault();
      return;
    }
    const first = controls[0];
    const last = controls.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className={`modal-layer ${layerClassName}`}
      role="presentation"
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
      onKeyDown={(event) => { if (event.target === event.currentTarget && event.key === 'Escape') onClose(); }}
    >
      <dialog
        open
        ref={surfaceRef}
        className={className}
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {children}
      </dialog>
    </div>,
    document.body
  );
}

function lockPage(root: HTMLElement | null) {
  lockedScrollY = window.scrollY;
  previousRootOverflow = document.documentElement.style.overflow;
  previousBodyStyles = {
    position: document.body.style.position,
    top: document.body.style.top,
    width: document.body.style.width,
    overflow: document.body.style.overflow
  };
  document.documentElement.style.overflow = 'hidden';
  Object.assign(document.body.style, { position: 'fixed', top: `-${lockedScrollY}px`, width: '100%', overflow: 'hidden' });
  if (root) root.inert = true;
}

function unlockPage(root: HTMLElement | null) {
  if (root) root.inert = false;
  document.documentElement.style.overflow = previousRootOverflow;
  Object.assign(document.body.style, previousBodyStyles);
  window.scrollTo({ top: lockedScrollY, behavior: 'instant' });
}
